import { Container, Sprite, Texture, Assets, type Spritesheet } from "pixi.js";
import { idx, type Layout } from "@empire/core-sim";
import { gridToScreen, TILE_W, TILE_H } from "./iso.js";
import { openEdges, type Edge } from "./road.js";
import { towerScale } from "./presentation.js";

const CELL = 80;
const PAD = 90;
const RESOLUTION = 2;
const DELTA: Record<Edge, readonly [number, number]> = {
  NE: [0, -1],
  SE: [1, 0],
  SW: [0, 1],
  NW: [-1, 0],
};
const noise = (x: number, y: number): number => {
  const v = Math.sin(x * 127.1 + y * 311.7) * 43758.5453;
  return v - Math.floor(v);
};

/** Uses only the layout the viewer already knows; it never discovers hidden roads. */
export function roadSegments(layout: Layout): { x: number; y: number; dx: number; dy: number }[] {
  const segments = [];
  for (let y = 0; y < layout.size; y++)
    for (let x = 0; x < layout.size; x++) {
      if (!["road", "mud", "barricade"].includes(layout.tiles[idx(x, y, layout.size)]?.kind ?? ""))
        continue;
      const edges = openEdges(layout, x, y);
      // A lone tile is still a road pad, including while the player draws a maze.
      if (!edges.length) segments.push({ x, y, dx: 0, dy: 0 });
      for (const edge of edges) {
        const [dx, dy] = DELTA[edge];
        segments.push({ x, y, dx: dx / 2, dy: dy / 2 });
      }
    }
  return segments;
}

export function sceneryPositions(layout: Layout): { x: number; y: number; tall: boolean }[] {
  const out = [];
  for (let side = 0; side < 4; side++)
    for (let i = 0; i < layout.size; i += 2) {
      const along = i + noise(i, side) * 0.7;
      const x = side === 0 ? -0.65 : side === 1 ? along : side === 2 ? layout.size - 0.38 : along;
      const y = side === 0 ? along : side === 1 ? -0.65 : side === 2 ? along : layout.size - 0.38;
      if (Math.hypot(x - layout.gate.x, y - layout.gate.y) < 2.7) continue;
      if (side >= 2 && noise(i, side + 8) > 0.45) continue;
      out.push({ x, y, tall: side < 2 });
    }
  return out;
}

let material: Promise<HTMLImageElement | null> | null = null;
let scenery: Promise<Spritesheet | null> | null = null;
export async function loadBattlefield(): Promise<Battlefield | null> {
  material ??= new Promise((resolve) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => resolve(null);
    image.src = `${import.meta.env.BASE_URL}art/ancient-meadow.webp`;
  });
  scenery ??= Assets.load<Spritesheet>(`${import.meta.env.BASE_URL}art/ancient-village.json`).catch(
    () => null
  );
  const [image, decor] = await Promise.all([material, scenery]);
  return image ? new Battlefield(image, decor) : null;
}

/** A private, reusable ground texture. It updates on layout edits, never on replay frames. */
export class Battlefield extends Container {
  private readonly canvas = document.createElement("canvas");
  private readonly surface = document.createElement("canvas");
  private texture: Texture | null = null;
  private picture: Sprite | null = null;
  private readonly decorations = new Container();
  private sceneryKey = "";
  private size = 0;
  constructor(
    private readonly material: HTMLImageElement,
    private readonly art: Spritesheet | null
  ) {
    super();
    this.eventMode = "none";
    this.addChild(this.decorations);
  }

  update(layout: Layout): void {
    const size = layout.size;
    const w = size * TILE_W + PAD * 2;
    const h = size * TILE_H + PAD * 2;
    if (this.size !== size) {
      this.canvas.width = w * RESOLUTION;
      this.canvas.height = h * RESOLUTION;
      this.surface.width = this.surface.height = size * CELL;
      this.size = size;
    }
    const g = this.surface.getContext("2d")!;
    const side = this.surface.width;
    g.clearRect(0, 0, side, side);
    g.drawImage(this.material, 0, 0, side, side);
    g.fillStyle = "#8e9b6710";
    g.fillRect(0, 0, side, side);
    const segments = roadSegments(layout);
    const path = new Path2D();
    for (const { x, y, dx, dy } of segments) {
      path.moveTo((x + 0.5) * CELL, (y + 0.5) * CELL);
      path.lineTo((x + 0.5 + dx) * CELL + (dx === 0 && dy === 0 ? 0.01 : 0), (y + 0.5 + dy) * CELL);
    }
    g.lineCap = "round";
    g.lineJoin = "round";
    for (const [width, color] of [
      [65, "#66543955"],
      [59, "#8b7859"],
      [53, "#c6b491"],
      [43, "#b5a383"],
    ] as const) {
      g.lineWidth = width;
      g.strokeStyle = color;
      g.stroke(path);
    }
    // Pavers are clipped to the actual road surface, including incomplete junctions.
    g.lineWidth = 34;
    for (let row = 0; row < side / 16; row++)
      for (let col = 0; col < side / 24; col++) {
        const x = col * 24 + (row % 2) * 12,
          y = row * 16;
        if (!g.isPointInStroke(path, x + 10, y + 7)) continue;
        g.fillStyle = ["#bbae8e", "#b8a98a", "#c9bb9c", "#c6b79a", "#ad9e80"][(row + col) % 5]!;
        g.fillRect(x + 1, y + 1, 21, 13);
        g.strokeStyle = "#83725770";
        g.lineWidth = 0.7;
        g.strokeRect(x + 1, y + 1, 21, 13);
        g.lineWidth = 34;
      }
    for (let y = 0; y < size; y++)
      for (let x = 0; x < size; x++) {
        const tile = layout.tiles[idx(x, y, size)];
        if (tile?.kind === "mud") {
          g.fillStyle = "#654329b0";
          g.fillRect(x * CELL + 5, y * CELL + 5, CELL - 10, CELL - 10);
          for (let k = 0; k < 4; k++) {
            g.fillStyle = "#352c2465";
            g.beginPath();
            g.ellipse(
              (x + 0.25 + noise(x + k, y) * 0.5) * CELL,
              (y + 0.2 + k * 0.15) * CELL,
              13,
              4,
              -0.2,
              0,
              Math.PI * 2
            );
            g.fill();
          }
        }
        if (tile?.kind === "tower_slot" || tile?.kind === "core") {
          const inset = tile.kind === "core" ? 0 : 6;
          g.fillStyle = tile.tower || tile.kind === "core" ? "#b6a384" : "#998d71";
          g.fillRect(x * CELL + inset, y * CELL + inset, CELL - 2 * inset, CELL - 2 * inset);
          g.strokeStyle = "#e0c9a180";
          g.lineWidth = 2;
          g.strokeRect(x * CELL + inset, y * CELL + inset, CELL - 2 * inset, CELL - 2 * inset);
          g.strokeStyle = "#73634860";
          g.lineWidth = 1;
          for (let k = 1; k < 4; k++) {
            g.beginPath();
            g.moveTo(x * CELL + inset, (y + k / 4) * CELL);
            g.lineTo((x + 1) * CELL - inset, (y + k / 4) * CELL);
            g.stroke();
          }
        }
      }
    const c = this.canvas.getContext("2d")!;
    c.setTransform(RESOLUTION, 0, 0, RESOLUTION, (w * RESOLUTION) / 2, PAD * RESOLUTION);
    c.clearRect(-w / 2, -PAD, w, h);
    // The playable surface belongs to the landscape; the editor grid still
    // marks its precise limits. Feather only the edge, preserving road centres.
    g.save();
    g.globalCompositeOperation = "destination-in";
    for (const vertical of [false, true]) {
      const fade = g.createLinearGradient(0, 0, vertical ? 0 : side, vertical ? side : 0);
      fade.addColorStop(0, "#fff0");
      fade.addColorStop(0.015, "#fff");
      fade.addColorStop(0.985, "#fff");
      fade.addColorStop(1, "#fff0");
      g.fillStyle = fade;
      g.fillRect(0, 0, side, side);
    }
    g.restore();
    const top = gridToScreen(-0.5, -0.5);
    c.save();
    c.transform(
      TILE_W / 2 / CELL,
      TILE_H / 2 / CELL,
      -TILE_W / 2 / CELL,
      TILE_H / 2 / CELL,
      top.x,
      top.y
    );
    c.drawImage(this.surface, 0, 0);
    c.restore();
    // Contact shadows stay planted while the tower picture recoils above them.
    for (let y = 0; y < size; y++)
      for (let x = 0; x < size; x++) {
        const tower = layout.tiles[idx(x, y, size)]?.tower;
        if (!tower) continue;
        const scale = towerScale(tower.kind, false);
        const at = gridToScreen(x, y);
        c.fillStyle = "#17231844";
        c.beginPath();
        c.ellipse(at.x + 10 * scale, at.y + 5 * scale, 29 * scale, 10 * scale, 0, 0, Math.PI * 2);
        c.fill();
      }
    if (!this.texture) {
      this.texture = Texture.from(this.canvas);
      this.picture = new Sprite(this.texture);
      this.picture.scale.set(1 / RESOLUTION);
      this.addChildAt(this.picture, 0);
    } else this.texture.source.update();
    this.picture!.position.set(-w / 2, -PAD);
    this.drawScenery(layout);
  }

  private drawScenery(layout: Layout): void {
    const key = `${layout.size}:${layout.gate.x}:${layout.gate.y}`;
    if (key === this.sceneryKey) return;
    this.sceneryKey = key;
    for (const child of this.decorations.removeChildren()) child.destroy();
    if (!this.art) return;
    sceneryPositions(layout).forEach(({ x, y, tall }, i) => {
      const name = tall
        ? ["decor_treeHigh", "decor_treeHighRound", "decor_treeCrooked"][i % 3]!
        : "decor_rockSmall";
      const texture = this.art?.textures[name];
      if (!texture) return;
      const sprite = new Sprite(texture);
      sprite.anchor.set(texture.defaultAnchor?.x ?? 0.5, texture.defaultAnchor?.y ?? 1);
      const at = gridToScreen(x, y);
      sprite.position.set(at.x, at.y);
      sprite.scale.set(tall ? 0.65 + noise(x, y) * 0.2 : 0.65);
      sprite.alpha = 0.88;
      this.decorations.addChild(sprite);
    });
  }

  dispose(): void {
    this.destroy({ children: true });
    this.texture?.destroy(true); // Only the private canvas, never a shared atlas.
    this.texture = null;
  }
}
